import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchDrpClientFeedback } from "../../../store/slices/drpClientFeedbackSlice";
import { DRP_SECTIONS, DRP_LIKERT_LEGEND } from "../../../pages/DrpClientFeedback/drpClientFeedbackConfig";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-GB") : "—");

export default function DrpClientFeedbackPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentDrpClientFeedback, isLoading, error } = useSelector((s) => s.drpClientFeedback);

  useEffect(() => {
    if (id) dispatch(fetchDrpClientFeedback(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentDrpClientFeedback) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => { clearTimeout(t); window.onafterprint = null; };
    }
  }, [currentDrpClientFeedback, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading DRP Client Feedback print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentDrpClientFeedback) return null;

  const d = currentDrpClientFeedback;
  const mark = (key, n) => (Number(d[key]) === n ? "☑" : "☐");

  return (
    <>
      <style jsx global>{`
        body { margin: 0 !important; padding: 0 !important; }
        ${printFooterStyles}
        @media print {
          @page { size: A4; margin: 12mm 12mm; }
          body { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .section, table, tr { page-break-inside: avoid; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          table { border-collapse: collapse !important; }
          table, th, td { border: 1px solid #000 !important; }
          th, .subhead td { background: #e9e9e9 !important; }
          .legend { background: #fafafa !important; border: 1px solid #000 !important; }
        }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .drp-print, .drp-print * { font-family: "Times New Roman", Times, serif; color: #000; }
        .drp-print { width: 100%; max-width: 820px; margin: 0 auto; padding: 16px; font-size: 10pt; line-height: 1.3; }
        .header-logo { text-align: center; margin-bottom: 4px; }
        .header-logo img { height: 58px; }
        .doc-org { text-align: center; font-size: 13pt; font-weight: bold; margin: 0; }
        .doc-prog { text-align: center; font-size: 11pt; font-weight: bold; margin: 0; }
        .doc-title { text-align: center; font-size: 12pt; font-weight: bold; text-transform: uppercase; margin: 0 0 8px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
        td, th { border: 1px solid #000; padding: 3px 6px; font-size: 9pt; vertical-align: top; }
        th { background: #e9e9e9; text-align: center; }
        .subhead td { background: #e9e9e9; font-weight: bold; }
        .lbl { font-weight: bold; background: #f7f7f7; width: 22%; }
        .legend { border: 1px solid #000; background: #fafafa; padding: 5px 7px; margin-bottom: 8px; font-size: 8.5pt; }
        .section-title { font-weight: bold; font-size: 10pt; background: #e9e9e9; border: 1px solid #000; padding: 3px 6px; text-transform: uppercase; }
        .notes-box { border: 1px solid #000; min-height: 38px; padding: 4px 6px; white-space: pre-wrap; font-size: 9pt; margin-bottom: 6px; }
        .qcol { text-align: left; }
        .mark { text-align: center; width: 8%; font-size: 11pt; }
        .footer { text-align: center; font-size: 8.5pt; margin-top: 10px; border-top: 1px solid #000; padding-top: 4px; }
      `}</style>

      <PrintWrapper>
        <div className="drp-print">
          <div className="header-logo"><img src={logo} alt="The Recovery House" /></div>
          <div className="doc-org">THE RECOVERY HOUSE (TRH)</div>
          <div className="doc-prog">DAY REHAB PROGRAMME (DRP)</div>
          <div className="doc-title">Client Feedback &amp; Discharge Interview Form</div>

          {/* Basic info */}
          <table>
          <tbody>
            <tr><td className="lbl">Client Name</td><td>{val(d.client_name)}</td><td className="lbl">MR No</td><td>{val(d.mr_no)}</td></tr>
            <tr><td className="lbl">Date of Admission</td><td>{fmt(d.date_of_admission)}</td><td className="lbl">Date of Discharge</td><td>{fmt(d.date_of_discharge)}</td></tr>
          </tbody>
        </table>

        {/* Legend */}
        <div className="legend">
          <strong>Likert Scale for all items:</strong>{" "}
          {DRP_LIKERT_LEGEND.map((l) => `${l.n} = ${l.label}`).join("  |  ")}
        </div>

        {/* Sections */}
        {DRP_SECTIONS.map((section) => (
          <table key={section.title}>
            <thead>
              <tr>
                <th className="qcol" style={{ textAlign: "left" }}>{section.title}</th>
                {[1, 2, 3, 4, 5].map((n) => (<th style={{ width: "7%" }} key={n}>{n}</th>))}
              </tr>
            </thead>
            <tbody>
              {section.questions.map((q) => (
                <React.Fragment key={q.key}>
                  {q.subheading && (
                    <tr className="subhead"><td colSpan={6}>{q.subheading}</td></tr>
                  )}
                  <tr>
                    <td className="qcol">{q.label}</td>
                    <td className="mark">{mark(q.key, 1)}</td>
                    <td className="mark">{mark(q.key, 2)}</td>
                    <td className="mark">{mark(q.key, 3)}</td>
                    <td className="mark">{mark(q.key, 4)}</td>
                    <td className="mark">{mark(q.key, 5)}</td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        ))}

        {/* Additional feedback */}
        <div className="section-title">Section 14: Additional Feedback</div>
        <div style={{ fontSize: "9pt", margin: "4px 0 3px" }}>Please provide suggestions for improvement:</div>
        <div className="notes-box">{d.additional_feedback || ""}</div>

        {/* Office use */}
        <div className="section-title" style={{ marginTop: 10 }}>For Office Use Only</div>
        <table style={{ marginTop: 6 }}>
          <tbody>
            <tr>
              <td className="lbl">Reviewed by</td><td>{val(d.reviewed_by)}</td>
              <td className="lbl">Signature</td><td>{val(d.reviewer_signature)}</td>
              <td className="lbl">Date</td><td>{fmt(d.review_date)}</td>
            </tr>
          </tbody>
        </table>

        <div className="footer">CONFIDENTIAL — For Authorised Clinical Use Only</div>
      </div>
      </PrintWrapper>
    </>
  );
}
